import React, { useState, useEffect, useRef } from 'react';
import { get, post, put, del } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const PRIORITIES = [
  { key:'urgent', label:'Urgent', color:'#8F2E24', bg:'#FDEAEA' },
  { key:'high',   label:'High',   color:'#8B5E0A', bg:'#FFF3DC' },
  { key:'medium', label:'Medium', color:'#1E4FAD', bg:'#EDF4FF' },
  { key:'low',    label:'Low',    color:'#8A8A8A', bg:'#F0F0F0' },
];

const CATEGORIES = ['Production','Cutting','Printing','Packing','Delivery','Admin','Sales','Finance','General'];

const STATUSES = [
  { key:'pending',     label:'Pending',     color:'#8A8A8A', bg:'#F0F0F0' },
  { key:'in_progress', label:'In Progress', color:'#1E4FAD', bg:'#EDF4FF' },
  { key:'pending_verification', label:'Pending Verification', color:'#8B5E0A', bg:'#FFF3DC' },
  { key:'completed',   label:'Completed',   color:'#2E6B3A', bg:'#E8F5EB' },
  { key:'cancelled',   label:'Cancelled',   color:'#8F2E24', bg:'#FDEAEA' },
];

const getPriority = key => PRIORITIES.find(p => p.key===key) || PRIORITIES[2];
const getStatus = key => STATUSES.find(s => s.key===key) || STATUSES[0];

const PriorityBadge = ({ priority }) => {
  const p = getPriority(priority);
  return <span style={{display:'inline-flex',padding:'3px 8px',borderRadius:20,fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.05em',background:p.bg,color:p.color}}>{p.label}</span>;
};

const StatusBadge = ({ status }) => {
  const s = getStatus(status);
  return <span style={{display:'inline-flex',padding:'3px 8px',borderRadius:20,fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.05em',background:s.bg,color:s.color}}>{s.label}</span>;
};

const isOverdue = (task) => {
  if (task.status==='completed'||task.status==='cancelled') return false;
  if (!task.due_date) return false;
  return new Date(task.due_date) < new Date(new Date().toDateString());
};

export default function Tasks() {
  const { user, hasPermission } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState({});
  const [users, setUsers] = useState([]);
  const [jobOrders, setJobOrders] = useState([]);
  // dynamic task-management scope
  const [managementScope, setManagementScope] =
    useState({
      is_manager: false,
      has_direct_reports: false,
      direct_report_count: 0,
      can_create_tasks: false,
      can_create_production_tasks: false
    });
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('board');
  const [filters, setFilters] = useState({ status:'', priority:'', assigned_to:'', category:'' });
  const [showCreate, setShowCreate] = useState(false);
  const [viewTask, setViewTask] = useState(null);
  const [editTask, setEditTask] = useState(null);

  // TASK REPORTING-LINE AUTHORITY
  const canBrowseAllTasks = [
    'super_admin',
    'admin'
  ].includes(user?.role);

  const isTeamManager =
    managementScope.is_manager === true;

  const canFilterTasks =
    canBrowseAllTasks ||
    isTeamManager;

  const canManageTasks =
    user?.role === 'super_admin' ||
    isTeamManager;

  const canCreate =
    managementScope.can_create_tasks === true &&
    hasPermission('tasks:write');

  const canCreateProduction =
    managementScope
      .can_create_production_tasks === true;

  const canDelete = [
    'super_admin',
    'admin'
  ].includes(user?.role);

  useEffect(() => {
    get('/tasks/management-scope')
      .then(result =>
        setManagementScope(
          result.data || {
            is_manager: false,
            has_direct_reports: false,
            direct_report_count: 0,
            can_create_tasks: false,
            can_create_production_tasks: false
          }
        )
      )
      .catch(() =>
        setManagementScope({
          is_manager: false,
          has_direct_reports: false,
          direct_report_count: 0,
          can_create_tasks: false,
          can_create_production_tasks: false
        })
      );
  }, [user?.id]);

  const load = () => {
    setLoading(true);
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k,v]) => { if(v) qs.append(k,v); });
    Promise.all([
      get('/tasks?'+qs.toString()),
      get('/tasks/summary')
    ]).then(([t,s]) => {
      setTasks(t.data||[]);
      setSummary(s);
    }).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const staffRequest =
      canFilterTasks
        ? get('/tasks/team-assignees')
        : Promise.resolve({
            data: []
          });

    staffRequest
      .then(d =>
        setUsers(d.data || [])
      )
      .catch(() =>
        setUsers([])
      );

    get('/job-orders')
      .then(d =>
        setJobOrders(d.data || [])
      )
      .catch(() =>
        setJobOrders([])
      );
  }, [filters, canFilterTasks]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    try { await del('/tasks/'+id); load(); }
    catch (err) { alert(err.message); }
  };

  const handleStatusChange = async (task, newStatus) => {
    try {
      await put('/tasks/'+task.id, { ...task, status: newStatus,
        completed_at: newStatus==='completed' ? new Date().toISOString() : null });
      load();
    } catch (err) { alert(err.message); }
  };

  const boardColumns = STATUSES.filter(s => s.key !== 'cancelled').map(s => ({
    ...s,
    tasks: tasks.filter(t => t.status === s.key)
  }));

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">Tasks</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>Assign and track tasks across all staff</p>
        </div>
        <div className="flex-center gap-sm">
          <button className={'btn btn-sm '+(view==='board'?'btn-primary':'btn-outline')} onClick={()=>setView('board')}>Board</button>
          <button className={'btn btn-sm '+(view==='list'?'btn-primary':'btn-outline')} onClick={()=>setView('list')}>List</button>
          {canCreate && <button className="btn btn-primary" onClick={()=>setShowCreate(true)}>+ New Task</button>}
        </div>
      </div>

      {/* Summary tiles */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:20}}>
        {[
          {label:'Pending Tasks', value:summary.pending||0, color:'#556B2F'},
          {label:'Due Today', value:summary.due_today||0, color:'#8B5E0A'},
          {label:'Overdue', value:summary.overdue||0, color:'#8F2E24'},
          {label:'Completed This Month', value:summary.completed_this_month||0, color:'#2E6B3A'},
        ].map(t => (
          <div key={t.label} style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:8,padding:'12px 14px',boxShadow:'var(--shadow)'}}>
            <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:4}}>{t.label}</div>
            <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.6rem',fontWeight:700,color:t.color}}>{t.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="search-bar" style={{marginBottom:16,flexWrap:'wrap'}}>
        {canFilterTasks && (
          <select style={{fontSize:12,padding:'6px 10px',border:'1px solid var(--border-light)',borderRadius:6}}
            value={filters.assigned_to} onChange={e=>setFilters({...filters,assigned_to:e.target.value})}>
            <option value="">All Staff</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        )}
        <select style={{fontSize:12,padding:'6px 10px',border:'1px solid var(--border-light)',borderRadius:6}}
          value={filters.status} onChange={e=>setFilters({...filters,status:e.target.value})}>
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <select style={{fontSize:12,padding:'6px 10px',border:'1px solid var(--border-light)',borderRadius:6}}
          value={filters.priority} onChange={e=>setFilters({...filters,priority:e.target.value})}>
          <option value="">All Priorities</option>
          {PRIORITIES.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
        <select style={{fontSize:12,padding:'6px 10px',border:'1px solid var(--border-light)',borderRadius:6}}
          value={filters.category} onChange={e=>setFilters({...filters,category:e.target.value})}>
          <option value="">All Categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {Object.values(filters).some(v=>v) && (
          <button className="btn btn-ghost btn-sm" onClick={()=>setFilters({status:'',priority:'',assigned_to:'',category:''})}>Clear Filters</button>
        )}
      </div>

      {loading ? <div className="spinner"></div> : (
        view==='board' ? (
          /* Kanban Board */
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:16}}>
            {boardColumns.map(col => (
              <div key={col.key} style={{background:'#F2EFE7',borderRadius:10,padding:12,minHeight:200}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12,padding:'0 4px'}}>
                  <div style={{fontWeight:600,fontSize:13,color:'#3B2A21'}}>{col.label}</div>
                  <span style={{background:col.bg,color:col.color,padding:'2px 8px',borderRadius:20,fontSize:11,fontWeight:600}}>{col.tasks.length}</span>
                </div>
                {col.tasks.map(task => (
                  <div key={task.id}
                    style={{background:'#fff',borderRadius:8,padding:'12px 14px',marginBottom:10,
                      border:'1px solid var(--border-light)',boxShadow:'var(--shadow)',cursor:'pointer',
                      borderLeft:'3px solid '+getPriority(task.priority).color}}
                    onClick={()=>setViewTask(task)}>
                    <div style={{fontWeight:500,fontSize:13,color:'#3B2A21',marginBottom:6}}>{task.title}</div>
                    <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:6}}>
                      <PriorityBadge priority={task.priority} />
                      {task.category && <span style={{fontSize:9,padding:'3px 8px',borderRadius:20,background:'#F2EFE7',color:'#556B2F',fontWeight:600,textTransform:'uppercase'}}>{task.category}</span>}
                    </div>
                    {task.assigned_to_name && (
                      <div style={{fontSize:11,color:'#8A8A8A',marginBottom:4}}>→ {task.assigned_to_name}</div>
                    )}
                    {task.due_date && (
                      <div style={{fontSize:11,fontWeight:500,color:isOverdue(task)?'var(--unpaid)':'#8A8A8A'}}>
                        {isOverdue(task)?'⚠ OVERDUE: ':''}{task.due_date}
                      </div>
                    )}
                    {task.job_number && <div style={{fontSize:10,color:'#8A8A8A',marginTop:4}}>Job: {task.job_number}</div>}
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : (
          /* List View */
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>Task</th><th>Assigned To</th><th>Category</th>
                  <th>Priority</th><th>Due Date</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {tasks.length===0
                  ? <tr><td colSpan="7"><div className="empty-state"><h3>No tasks found</h3></div></td></tr>
                  : tasks.map(task => (
                    <tr key={task.id} style={{background:isOverdue(task)?'#FFF8F8':''}}>
                      <td>
                        <div style={{fontWeight:500,fontSize:13}}>{task.title}</div>
                        <div
                          style={{
                            fontSize:9,
                            fontWeight:700,
                            textTransform:'uppercase',
                            color:
                              task.task_type === 'production'
                                ? '#8F2E24'
                                : '#556B2F',
                            marginTop:2
                          }}
                        >
                          {task.task_type === 'production'
                            ? 'Production Task'
                            : 'General Task'}
                        </div>
                        {task.job_number && <div style={{fontSize:11,color:'#8A8A8A'}}>Job: {task.job_number}</div>}
                      </td>
                      <td style={{fontSize:13}}>{task.assigned_to_name||'Unassigned'}</td>
                      <td><span style={{fontSize:10,padding:'3px 8px',borderRadius:20,background:'#F2EFE7',color:'#556B2F',fontWeight:600,textTransform:'uppercase'}}>{task.category}</span></td>
                      <td><PriorityBadge priority={task.priority} /></td>
                      <td style={{fontSize:13,color:isOverdue(task)?'var(--unpaid)':'inherit',fontWeight:isOverdue(task)?600:400}}>
                        {task.due_date||'—'}{isOverdue(task)?' ⚠':''}
                      </td>
                      <td><StatusBadge status={task.status} /></td>
                      <td>
                        <div className="flex-center gap-sm">
                          <button className="btn btn-ghost btn-sm" onClick={()=>setViewTask(task)}>View</button>
                          {canManageTasks && <button className="btn btn-outline btn-sm" onClick={()=>setEditTask(task)}>Edit</button>}
                          {canDelete && <button className="btn btn-danger btn-sm" onClick={()=>handleDelete(task.id)}>Delete</button>}
                        </div>
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        )
      )}

      {showCreate && (
        <TaskModal
          users={users}
          jobOrders={jobOrders}
          userRole={user?.role}
          canCreateProduction={canCreateProduction}
          onClose={()=>setShowCreate(false)}
          onSaved={()=>{ setShowCreate(false); load(); }}
        />
      )}
      {editTask && (
        <TaskModal
          task={editTask}
          users={users}
          jobOrders={jobOrders}
          userRole={user?.role}
          canCreateProduction={canCreateProduction}
          onClose={()=>setEditTask(null)}
          onSaved={()=>{ setEditTask(null); load(); }}
        />
      )}
      {viewTask && (
        <ViewTaskModal
          task={viewTask}
          user={user}
          canManageTask={canManageTasks}
          onClose={()=>setViewTask(null)}
          onUpdated={()=>{ setViewTask(null); load(); }}
          onEdit={(t)=>{ setViewTask(null); setEditTask(t); }}
        />
      )}
    </div>
  );
}

function TaskModal({
  task,
  users,
  jobOrders,
  userRole,
  canCreateProduction,
  onClose,
  onSaved
}) {
  const isEdit = !!task?.id;
  const [additionalAssignees, setAdditionalAssignees] = useState([]);
  const [form, setForm] = useState({
    title: task?.title||'',
    description: task?.description||'',
    assigned_to: task?.assigned_to||'',
    due_date: task?.due_date||'',
    due_time: task?.due_time||'',
    priority: task?.priority||'medium',
    category: task?.category||'General',
    status: task?.status||'pending',
    task_type:
      task?.task_type ||
      (
        userRole === 'production_team_lead'
          ? 'production'
          : 'general'
      ),
    job_order_id: task?.job_order_id||'',
    invoice_id: task?.invoice_id||'',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!form.title.trim()) {
      setError('Task title is required');
      return;
    }

    if (!form.assigned_to) {
      setError('Select the employee responsible for this task');
      return;
    }

    if (
      form.task_type === 'production' &&
      !form.job_order_id
    ) {
      setError(
        'Select the job order for this production task'
      );
      return;
    }

    if (
      form.task_type === 'production' &&
      !canCreateProduction
    ) {
      setError(
        'You are not authorized to create or manage production tasks'
      );
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        additional_assignees:
          additionalAssignees
      };

      if (isEdit) {
        await put('/tasks/' + task.id, payload);
      } else {
        await post('/tasks', payload);
      }
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:580}}>
        <div className="modal-hdr">
          <span className="modal-title">{isEdit?'Edit Task':'New Task'}</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error mb-md">{error}</div>}
          <div className="form-group">
            <label className="form-label">Task Title *</label>
            <input className="form-control" placeholder="e.g. Cut 200 jute bags for BLC-2026-0015"
              value={form.title} onChange={e=>setForm({...form,title:e.target.value})} />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-control" rows={3}
              placeholder="Detailed instructions for the staff member..."
              value={form.description||''} onChange={e=>setForm({...form,description:e.target.value})} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Assign To</label>
              <select className="form-control" value={form.assigned_to} onChange={e=>setForm({...form,assigned_to:e.target.value})}>
                <option value="">Select staff</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role.replace('_',' ')})</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Category</label>
              <select className="form-control" value={form.category} onChange={e=>setForm({...form,category:e.target.value})}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
          <div className="form-group">
            <label className="form-label">Also Assign To (optional)</label>
            <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:6}}>
              {users.filter(u=>u.id!==form.assigned_to).map(u=>(
                <label key={u.id} style={{display:'flex',alignItems:'center',gap:4,fontSize:12,
                  padding:'4px 10px',borderRadius:20,cursor:'pointer',userSelect:'none',
                  background:additionalAssignees.includes(u.id)?'#556B2F':'#F2EFE7',
                  color:additionalAssignees.includes(u.id)?'#fff':'#3B2A21',
                  border:'1px solid '+(additionalAssignees.includes(u.id)?'#556B2F':'var(--border-light)')}}>
                  <input type="checkbox" style={{display:'none'}}
                    checked={additionalAssignees.includes(u.id)}
                    onChange={e=>{
                      if(e.target.checked) setAdditionalAssignees(prev=>[...prev,u.id]);
                      else setAdditionalAssignees(prev=>prev.filter(id=>id!==u.id));
                    }} />
                  {u.name.split(' ')[0]}
                </label>
              ))}
            </div>
            <div className="form-hint">Task will appear in all selected staff members' task lists</div>
          </div>
            <div className="form-group">
              <label className="form-label">Due Date</label>
              <input type="date" className="form-control" value={form.due_date||''}
                onChange={e=>setForm({...form,due_date:e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">Due Time</label>
              <input type="time" className="form-control" value={form.due_time||''}
                onChange={e=>setForm({...form,due_time:e.target.value})} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Priority</label>
              <select className="form-control" value={form.priority} onChange={e=>setForm({...form,priority:e.target.value})}>
                {PRIORITIES.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </div>
            {isEdit && (
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-control" value={form.status} onChange={e=>setForm({...form,status:e.target.value})}>
                  {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">
              Task Type *
            </label>

            <select
              className="form-control"
              value={form.task_type}
              onChange={e => {
                const taskType =
                  e.target.value;

                setForm({
                  ...form,
                  task_type: taskType,
                  job_order_id:
                    taskType === 'production'
                      ? form.job_order_id
                      : ''
                });
              }}
            >
              <option value="general">
                General Task
              </option>

              {canCreateProduction && (
                <option value="production">
                  Production Task
                </option>
              )}
            </select>

            <div
              style={{
                fontSize:10,
                color:'#8A8A8A',
                marginTop:5
              }}
            >
              {form.task_type === 'production'
                ? 'Production tasks must belong to an open job order.'
                : 'General tasks are not connected to production or material issuance.'}
            </div>
          </div>

          {form.task_type === 'production' && (
            <div className="form-group">
              <label className="form-label">
                Job Order *
              </label>

              <select
                className="form-control"
                value={form.job_order_id || ''}
                onChange={e =>
                  setForm({
                    ...form,
                    job_order_id:
                      e.target.value
                  })
                }
                required
              >
                <option value="">
                  Select an open job order
                </option>

                {jobOrders
                  .filter(job =>
                    ![
                      'ready',
                      'dispatched',
                      'cancelled',
                      'completed'
                    ].includes(job.status)
                  )
                  .map(job => (
                    <option
                      key={job.id}
                      value={job.id}
                    >
                      {job.job_number}
                      {job.customer_name
                        ? ` — ${job.customer_name}`
                        : ''}
                    </option>
                  ))}
              </select>
            </div>
          )}
        </div>
        <div className="modal-ftr">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving?'Saving...':isEdit?'Save Changes':'Create Task'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ViewTaskModal({
  task,
  user,
  canManageTask,
  onClose,
  onUpdated,
  onEdit
}) {
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const taskId = task.id;
  const fileRef = useRef(null);

  const getFileIcon = (fileType) => {
    if (['.jpg','.jpeg','.png','.gif','.webp'].includes(fileType)) return '🖼';
    if (fileType === '.pdf') return '📄';
    if (['.doc','.docx'].includes(fileType)) return '📝';
    if (fileType === '.zip') return '🗜';
    return '📎';
  };
  const [newComment, setNewComment] = useState('');
  const [posting, setPosting] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [completionNote, setCompletionNote] = useState(task.completion_note||'');

  // TASK VERIFICATION DECISION UI
  const [verificationNote, setVerificationNote] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [verificationHistory, setVerificationHistory] = useState([]);
  const [verificationLoading, setVerificationLoading] = useState(false);

  const loadComments = () => {
    get('/tasks/'+task.id+'/comments').then(d => setComments(d.data||[]));
  };

  useEffect(() => {
    loadComments();

    get('/tasks/'+task.id+'/attachments')
      .then(data =>
        setAttachments(data.data || [])
      )
      .catch(() => {});

    if (task.task_type === 'production') {
      get(
        '/tasks/' +
        task.id +
        '/verification-history'
      )
        .then(data =>
          setVerificationHistory(
            data.data || []
          )
        )
        .catch(() =>
          setVerificationHistory([])
        );
    }
  }, [task.id]);


  const canVerifyProductionTask =
    task.task_type === 'production' &&
    task.status === 'pending_verification' &&
    [
      'super_admin',
      'production_team_lead'
    ].includes(user?.role);

  const approveProductionTask = async () => {
    if (
      !window.confirm(
        'Confirm that you physically inspected and approve this task.'
      )
    ) {
      return;
    }

    setVerificationLoading(true);

    try {
      await put(
        '/tasks/' +
        task.id +
        '/verification/approve',
        {
          verification_note:
            verificationNote.trim()
        }
      );

      onUpdated();
    } catch (error) {
      alert(error.message);
    } finally {
      setVerificationLoading(false);
    }
  };

  const rejectProductionTask = async () => {
    const reason =
      rejectionReason.trim();

    if (reason.length < 5) {
      alert(
        'Enter a clear reason before returning the task.'
      );
      return;
    }

    if (
      !window.confirm(
        'Return this task to the worker for correction?'
      )
    ) {
      return;
    }

    setVerificationLoading(true);

    try {
      await put(
        '/tasks/' +
        task.id +
        '/verification/reject',
        {
          verification_rejection_reason:
            reason
        }
      );

      onUpdated();
    } catch (error) {
      alert(error.message);
    } finally {
      setVerificationLoading(false);
    }
  };


  const postComment = async () => {
    if (!newComment.trim()) return;
    setPosting(true);
    try {
      await post('/tasks/'+task.id+'/comments', { comment: newComment });
      setNewComment('');
      loadComments();
    } catch (err) { alert(err.message); } finally { setPosting(false); }
  };

  const updateStatus = async (newStatus) => {
    // Enforce completion note for own tasks
    if (newStatus === 'completed' && needsProof && (!completionNote || completionNote.trim().length < 5)) {
      alert('Please add a completion note describing what was done before marking this task complete.');
      return;
    }
    setUpdatingStatus(true);
    try {
      await put('/tasks/'+task.id, { ...task, status: newStatus, completion_note: completionNote });
      // Upload evidence if provided
      if (newStatus === 'completed' && evidenceFile) {
        const fd = new FormData();
        fd.append('evidence', evidenceFile);
        const token = localStorage.getItem('bb_token');
        await fetch('/api/tasks/'+task.id+'/evidence', {
          method: 'POST',
          headers: { Authorization: 'Bearer '+token },
          body: fd
        });
      }
      onUpdated();
    } catch (err) { alert(err.message); } finally { setUpdatingStatus(false); }
  };

  const viewOnlyRoles = ['tailor','production_assistant','support_staff','production_staff'];
  const isOwnTask =
    task.assigned_to === user?.id;

  const isSubordinateTask =
    task.assignee_manager_id === user?.id;
  const canUpdateStatus = user?.role === 'super_admin' || 
    (isOwnTask && !viewOnlyRoles.includes(user?.role)) ||
    isSubordinateTask;
  const needsProof = isOwnTask && user?.role !== 'super_admin';
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [uploadingEvidence, setUploadingEvidence] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('bb_token');
      const res = await fetch('/api/tasks/'+taskId+'/attachments', {
        method: 'POST',
        headers: { Authorization: 'Bearer '+token },
        body: fd
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      get('/tasks/'+taskId+'/attachments').then(d => setAttachments(d.data||[]));
    } catch (err) { alert(err.message); } finally { setUploading(false); }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await del('/tasks/'+taskId+'/attachments/'+attachmentId);
      get('/tasks/'+taskId+'/attachments').then(d => setAttachments(d.data||[]));
    } catch (err) { alert(err.message); }
  };
  const canComplete = ['super_admin','admin','production_supervisor','sales_manager','content_sales'].includes(user?.role);
  const isOverdueTask = isOverdue(task);

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:620}}>
        <div className="modal-hdr">
          <div style={{flex:1}}>
            <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
              <span className="modal-title" style={{fontSize:'1rem'}}>{task.title}</span>
              <PriorityBadge priority={task.priority} />
              <StatusBadge status={task.status} />
              {isOverdueTask && <span style={{fontSize:9,fontWeight:700,color:'var(--unpaid)',background:'#FDEAEA',padding:'3px 8px',borderRadius:20,textTransform:'uppercase'}}>OVERDUE</span>}
            </div>
          </div>
          <div className="flex-center gap-sm">
            {canManageTask && task.status !== 'pending_verification' && <button className="btn btn-outline btn-sm" onClick={()=>onEdit(task)}>Edit</button>}
            <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
          </div>
        </div>
        <div className="modal-body">
          {/* Task details */}
          <div style={{background:'#F2EFE7',borderRadius:8,padding:14,marginBottom:16}}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,fontSize:13}}>
              <div><span style={{color:'#8A8A8A'}}>Assigned to: </span><strong>{task.assigned_to_name||'Unassigned'}</strong></div>
              <div><span style={{color:'#8A8A8A'}}>Category: </span><strong>{task.category}</strong></div>
              <div>
                <span style={{color:'#8A8A8A'}}>
                  Task Type:{' '}
                </span>
                <strong>
                  {task.task_type === 'production'
                    ? 'Production'
                    : 'General'}
                </strong>
              </div>
              <div><span style={{color:'#8A8A8A'}}>Due: </span><strong style={{color:isOverdueTask?'var(--unpaid)':'inherit'}}>{task.due_date||'No deadline'}{task.due_time?' at '+task.due_time:''}</strong></div>
              <div><span style={{color:'#8A8A8A'}}>Created by: </span><strong>{task.assigned_by_name}</strong></div>
              {task.job_number && <div><span style={{color:'#8A8A8A'}}>Job Order: </span><strong>{task.job_number}</strong></div>}
              {task.status==='completed' && task.completed_at && (
                <div><span style={{color:'#8A8A8A'}}>Completed: </span><strong>{new Date(task.completed_at).toLocaleDateString('en-NG')}</strong></div>
              )}
            </div>
          </div>

          {task.description && (
            <div style={{marginBottom:16,padding:'12px 14px',background:'#fff',border:'1px solid var(--border-light)',borderRadius:8}}>
              <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:6}}>Description</div>
              <div style={{fontSize:13,color:'#3B2A21',lineHeight:1.6}}>{task.description}</div>
            </div>
          )}

          {/* Production verification workflow */}
            {task.task_type === 'production' &&
              task.status ===
                'pending_verification' && (
              <div style={{
                background:'#FFF3DC',
                border:'2px solid #A8893D',
                borderRadius:8,
                padding:14,
                marginBottom:16
              }}>
                <div style={{
                  fontWeight:700,
                  color:'#8B5E0A',
                  marginBottom:8
                }}>
                  Awaiting Physical Verification
                </div>

                <div style={{
                  fontSize:12,
                  color:'#3B2A21',
                  marginBottom:12
                }}>
                  <strong>
                    Worker’s submission:
                  </strong>{' '}
                  {task.submission_note ||
                    'No submission note supplied'}
                </div>

                {canVerifyProductionTask ? (
                  <>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={verificationNote}
                      onChange={event =>
                        setVerificationNote(
                          event.target.value
                        )
                      }
                      placeholder="Verification note (optional when approving)"
                      style={{marginBottom:10}}
                    />

                    <textarea
                      className="form-control"
                      rows={2}
                      value={rejectionReason}
                      onChange={event =>
                        setRejectionReason(
                          event.target.value
                        )
                      }
                      placeholder="Reason is compulsory when returning the task"
                      style={{marginBottom:10}}
                    />

                    <div className="flex-center gap-sm">
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={
                          approveProductionTask
                        }
                        disabled={
                          verificationLoading
                        }
                      >
                        Approve Completed Task
                      </button>

                      <button
                        className="btn btn-danger btn-sm"
                        onClick={
                          rejectProductionTask
                        }
                        disabled={
                          verificationLoading ||
                          rejectionReason
                            .trim().length < 5
                        }
                      >
                        Return for Correction
                      </button>
                    </div>
                  </>
                ) : (
                  <div style={{
                    fontSize:11,
                    color:'#8B5E0A'
                  }}>
                    Only the responsible Production
                    Team Lead or Super Admin can
                    make this decision.
                  </div>
                )}
              </div>
            )}

            {task.task_type === 'production' &&
              task.verification_rejection_reason &&
              task.status === 'in_progress' && (
              <div style={{
                background:'#FDEAEA',
                border:'1px solid #D97066',
                borderRadius:8,
                padding:12,
                marginBottom:16
              }}>
                <div style={{
                  fontWeight:700,
                  color:'#8F2E24',
                  marginBottom:4
                }}>
                  Returned for Correction
                </div>

                <div style={{
                  fontSize:12,
                  color:'#3B2A21'
                }}>
                  {
                    task
                      .verification_rejection_reason
                  }
                </div>
              </div>
            )}

            {/* General tasks retain normal controls */}
            {task.task_type !== 'production' &&
              canUpdateStatus &&
              task.status !== 'completed' &&
              task.status !== 'cancelled' && (
              <div style={{
                padding:14,
                background:'#F2EFE7',
                borderRadius:8,
                marginBottom:16
              }}>
                <div style={{
                  fontSize:9,
                  fontWeight:600,
                  letterSpacing:'.1em',
                  textTransform:'uppercase',
                  color:'#8A8A8A',
                  marginBottom:10
                }}>
                  Update General Task
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Completion Note
                  </label>

                  <input
                    className="form-control"
                    placeholder="Add a note about completion..."
                    value={completionNote}
                    onChange={event =>
                      setCompletionNote(
                        event.target.value
                      )
                    }
                  />
                </div>

                <div className="flex-center gap-sm">
                  {task.status === 'pending' && (
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() =>
                        updateStatus(
                          'in_progress'
                        )
                      }
                      disabled={updatingStatus}
                    >
                      Start Task
                    </button>
                  )}

                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() =>
                      updateStatus('completed')
                    }
                    disabled={updatingStatus}
                  >
                    Mark Complete
                  </button>

                  {canManageTask && (
                    <button
                      className="btn btn-outline btn-sm"
                      style={{
                        color:'var(--unpaid)',
                        borderColor:
                          'var(--unpaid)'
                      }}
                      onClick={() =>
                        updateStatus('cancelled')
                      }
                      disabled={updatingStatus}
                    >
                      Cancel Task
                    </button>
                  )}
                </div>
              </div>
            )}

            {task.task_type === 'production' &&
              verificationHistory.length > 0 && (
              <div style={{
                border:
                  '1px solid var(--border-light)',
                borderRadius:8,
                padding:12,
                marginBottom:16
              }}>
                <div style={{
                  fontSize:9,
                  fontWeight:700,
                  letterSpacing:'.1em',
                  textTransform:'uppercase',
                  color:'#8A8A8A',
                  marginBottom:8
                }}>
                  Verification History
                </div>

                {verificationHistory.map(
                  event => (
                    <div
                      key={event.id}
                      style={{
                        fontSize:11,
                        padding:'7px 0',
                        borderBottom:
                          '1px solid #F2EFE7'
                      }}
                    >
                      <strong style={{
                        textTransform:
                          'capitalize'
                      }}>
                        {event.action}
                      </strong>
                      {' — '}
                      {event.actor_name}
                      {' — '}
                      {new Date(
                        event.created_at
                      ).toLocaleString(
                        'en-NG'
                      )}

                      {event.note && (
                        <div style={{
                          color:'#666',
                          marginTop:2
                        }}>
                          {event.note}
                        </div>
                      )}
                    </div>
                  )
                )}
              </div>
            )}

            {/* Attachments */}
          <div style={{marginTop:16,borderTop:'1px solid #F2EFE7',paddingTop:16}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
              <div style={{fontSize:10,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A'}}>
                Attachments ({attachments.length})
              </div>
              <div>
                <input type="file" ref={fileRef} style={{display:'none'}} onChange={handleUpload}
                  accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.zip" />
                <button className="btn btn-ghost btn-sm" style={{fontSize:11}}
                  onClick={()=>fileRef.current.click()} disabled={uploading}>
                  {uploading?'Uploading...':'+ Attach File'}
                </button>
              </div>
            </div>
            {attachments.length === 0 ? (
              <div style={{fontSize:12,color:'#8A8A8A',textAlign:'center',padding:'10px 0'}}>
                No attachments yet
              </div>
            ) : (
              <div style={{display:'flex',flexDirection:'column',gap:6}}>
                {attachments.map(att => (
                  <div key={att.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',
                    background:'#F2EFE7',borderRadius:8,padding:'8px 12px'}}>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <span style={{fontSize:18}}>{getFileIcon(att.file_type)}</span>
                      <div>
                        <div style={{fontSize:12,fontWeight:500,color:'#3B2A21'}}>{att.original_name}</div>
                        <div style={{fontSize:10,color:'#8A8A8A'}}>
                          {att.uploaded_by_name} • {new Date(att.created_at).toLocaleDateString('en-NG')}
                          {att.file_size && ' • '+(att.file_size/1024).toFixed(0)+'KB'}
                        </div>
                      </div>
                    </div>
                    <div style={{display:'flex',gap:6}}>
                      <a href={'/api/tasks/'+taskId+'/attachments/'+att.filename}
                        className="btn btn-ghost btn-sm" style={{fontSize:10}} download={att.original_name}>
                        ⬇ Download
                      </a>
                      {['.jpg','.jpeg','.png','.gif'].includes(att.file_type) && (
                        <a href={'/api/tasks/'+taskId+'/attachments/'+att.filename}
                          target="_blank" rel="noreferrer"
                          className="btn btn-ghost btn-sm" style={{fontSize:10}}>
                          👁 View
                        </a>
                      )}
                      <button className="btn btn-danger btn-sm" style={{fontSize:10}}
                        onClick={()=>handleDeleteAttachment(att.id)}>✕</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {task.completion_note && (
            <div style={{marginBottom:16,padding:'10px 14px',background:'#E8F5EB',borderRadius:8,border:'1px solid #B8DEC0'}}>
              <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#2E6B3A',marginBottom:4}}>Completion Note</div>
              <div style={{fontSize:13,color:'#3B2A21'}}>{task.completion_note}</div>
            </div>
          )}

          {/* Comments */}
          <div>
            <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:10}}>Comments & Updates</div>
            {comments.length===0
              ? <div style={{fontSize:12,color:'#8A8A8A',fontStyle:'italic',marginBottom:12}}>No comments yet</div>
              : <div style={{marginBottom:12}}>
                  {comments.map(c => (
                    <div key={c.id} style={{padding:'10px 12px',background:'#fff',border:'1px solid var(--border-light)',borderRadius:8,marginBottom:8}}>
                      <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                        <span style={{fontSize:12,fontWeight:500,color:'#3B2A21'}}>{c.created_by_name}</span>
                        <span style={{fontSize:11,color:'#8A8A8A'}}>{new Date(c.created_at).toLocaleString('en-NG')}</span>
                      </div>
                      <div style={{fontSize:13,color:'#555'}}>{c.comment}</div>
                    </div>
                  ))}
                </div>
            }
            <div style={{display:'flex',gap:8}}>
              <input className="form-control" style={{flex:1}} placeholder="Add a comment or update..."
                value={newComment} onChange={e=>setNewComment(e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&!e.shiftKey&&postComment()} />
              <button className="btn btn-primary btn-sm" onClick={postComment} disabled={posting||!newComment.trim()}>
                {posting?'...':'Post'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
